import React, { useEffect, useRef, useState } from 'react'
import type { Editor } from '@tiptap/react'
import {
  CANVAS_TEXT_MARKER_BOX,
  canvasTextMarkerShape,
  type CanvasTextMarkerShapeSpec,
} from '../ProjectCanvas/utils/canvasTextMarkerShape'
import { titleMarkerLines, type TitleMarkerPieceRects } from './titleMarkerLines'

export interface TitleMarkerLayerProps {
  /** Der Editor des Titels — `null`, solange er noch nicht steht. */
  editor: Editor | null
  /** Die Form der Markerstriche (`PROJECT_TITLE_MARKER`). */
  spec: CanvasTextMarkerShapeSpec
}

interface Segment {
  key: string
  left: number
  top: number
  width: number
  height: number
  color: string
  mask: string
}

const emOf = (value: string) => parseFloat(value) || 0

/**
 * Die **Marker des Projekttitels** als eigene Ebene hinter dem Schreibfeld
 * (`.claude/tasks/title-marker-line-wrap.md`). Bricht ein markierter Titel
 * um, zeichnet sie **je Zeile einen eigenen Markerstrich** — ein `::before`
 * am Stück spannte sich über beide Zeilen und lag als ein Balken in der Lücke
 * (gemeldet 2026-09-26).
 *
 * Sie misst die Stücke (`[data-marker-stroke]`, ein Rechteck je Zeile), legt
 * sie je Markerstrich und Zeile zusammen (`titleMarkerLines`) und zeichnet
 * jeden Strich in der gemessenen Breite (`canvasTextMarkerShape`). Farbe,
 * Versatz und Schrift-Ausgleich liest sie am Stück ab.
 *
 * Neu gemessen wird bei jeder Änderung des Titels (auch der Vorschau), bei
 * jeder Breitenänderung und sobald die Schriften geladen sind. Verborgen
 * (TabStack, `display: none`) misst sie nichts.
 *
 * @example
 * <TitleMarkerLayer editor={editor} spec={PROJECT_TITLE_MARKER} />
 */
export const TitleMarkerLayer: React.FC<TitleMarkerLayerProps> = ({ editor, spec }) => {
  const layerRef = useRef<HTMLDivElement>(null)
  const [segments, setSegments] = useState<Segment[]>([])

  useEffect(() => {
    const layer = layerRef.current
    const host = layer?.parentElement
    if (!editor || !layer || !host) return

    const measure = () => {
      const origin = host.getBoundingClientRect()
      if (origin.width === 0) return
      const pieces: TitleMarkerPieceRects[] = []
      const looks = new Map<number, { color: string; shift: number; fontSize: number }>()
      editor.view.dom.querySelectorAll<HTMLElement>('[data-marker-stroke]').forEach(element => {
        const stroke = Number(element.dataset.markerStroke)
        const seed = Number(element.dataset.markerSeed)
        const rects = Array.from(element.getClientRects(), rect => ({
          left: rect.left - origin.left,
          right: rect.right - origin.left,
          top: rect.top - origin.top,
          bottom: rect.bottom - origin.top,
        }))
        pieces.push({ stroke, seed, rects })
        if (!looks.has(stroke)) {
          const style = getComputedStyle(element)
          looks.set(stroke, {
            color: style.getPropertyValue('--highlight').trim(),
            shift: emOf(style.getPropertyValue('--marker-offset')) + emOf(style.getPropertyValue('--marker-font-shift')),
            fontSize: parseFloat(style.fontSize) || 16,
          })
        }
      })

      const next = titleMarkerLines(pieces).map((line): Segment => {
        const look = looks.get(line.stroke)!
        const fs = look.fontSize
        const shape = canvasTextMarkerShape(spec, {
          seed: line.seed,
          text: '',
          lengthEm: (line.right - line.left) / fs,
          start: true,
          end: true,
          straight: false,
        })
        const height = CANVAS_TEXT_MARKER_BOX * fs
        return {
          key: `${line.stroke}:${line.lineOfStroke}`,
          left: line.left - shape.startOverhang * fs,
          top: (line.top + line.bottom) / 2 - height / 2 + look.shift * fs,
          width: line.right - line.left + (shape.startOverhang + shape.endOverhang) * fs,
          height,
          color: look.color,
          mask: shape.mask,
        }
      })
      setSegments(current => (JSON.stringify(current) === JSON.stringify(next) ? current : next))
    }

    let frame = 0
    const schedule = () => {
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(measure)
    }

    measure()
    editor.on('transaction', schedule)
    const observer = new ResizeObserver(schedule)
    observer.observe(host)
    let alive = true
    void document.fonts?.ready.then(() => { if (alive) schedule() })
    return () => {
      alive = false
      cancelAnimationFrame(frame)
      editor.off('transaction', schedule)
      observer.disconnect()
    }
  }, [editor, spec])

  return (
    <div ref={layerRef} className="project-title__markers" aria-hidden="true">
      {segments.map(segment => (
        <div
          key={segment.key}
          className="project-title__marker"
          style={{
            '--marker-left': `${segment.left}px`,
            '--marker-top': `${segment.top}px`,
            '--marker-width': `${segment.width}px`,
            '--marker-height': `${segment.height}px`,
            '--marker-color': segment.color,
            '--marker-mask': segment.mask,
            '--marker-alpha': spec.opacity,
          } as React.CSSProperties}
        />
      ))}
    </div>
  )
}
